import React, { Key } from 'react';
import {
  MultiSelectValue,
  MultiSelectValueRenderProps,
  PrimitiveMultiSelect,
  PrimitiveMultiSelectProps,
} from './PrimitiveMultiSelect';
import { FieldError, Selection, Text, ValidationResult } from 'react-aria-components';
import { Label } from '../Label';
import { Button } from '../../AriaButton';
import { Popover } from '../../Popover';
import { ListBox } from '../ListBox';
import { CollectionPortal } from './Collection';
import { composeTailwindRenderProps } from '@/utils/utils';
import { ChevronDown } from 'lucide-react';
import { FieldGroup } from '../ComboBox/Field';
import { MultiSelectAllCheckbox } from './MultiSelectAllCheckbox';

interface MultiSelectProps<T extends object, IdKey extends keyof T>
  extends Omit<PrimitiveMultiSelectProps<T>, 'children'> {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  items?: Iterable<T>;
  children: React.ReactNode | ((item: T) => React.ReactNode);
  selectedItemsRender?: (values: MultiSelectValueRenderProps<T>) => React.ReactNode;
  keyProp: IdKey;
}

export function MultiSelect<T extends object, IdKeyProp extends keyof T>({
  label,
  description,
  errorMessage,
  children,
  items,
  keyProp,
  onSelectionChange,
  selectedItemsRender,
  ...props
}: MultiSelectProps<T, IdKeyProp>) {
  const handleOnSelectionChangeWithAll = (selection: Selection) => {
    if (onSelectionChange) {
      if (selection === 'all') {
        if (items) {
          const it = Array.from(items);
          const allKeys = new Set(it.map((item) => item[keyProp] as Key));
          onSelectionChange(allKeys);
        }
      } else {
        onSelectionChange(selection);
      }
    }
  };

  return (
    <PrimitiveMultiSelect
      {...props}
      items={items}
      className={composeTailwindRenderProps(
        props.className,
        'group flex flex-col gap-1 text-xs min-w-[275px]',
      )}
      onSelectionChange={handleOnSelectionChangeWithAll}>
      <CollectionPortal items={items}>{children}</CollectionPortal>
      {label && <Label>{label}</Label>}
      <FieldGroup className="flex w-full">
        <Button className="rounded outline-offset-0 bg-zinc-100 z-10 flex-1 h-full" variant="icon">
          <MultiSelectValue className="flex-1 text-left px-2 text-xs">
            {selectedItemsRender}
          </MultiSelectValue>
          <ChevronDown aria-hidden className="w-4 h-4 text-zinc-800" />
        </Button>
      </FieldGroup>
      {description && <Text slot="description">{description}</Text>}
      <FieldError>{errorMessage}</FieldError>
      <Popover className="min-w-[var(--trigger-width)] text-xs" offset={0}>
        <>
          <MultiSelectAllCheckbox />
          <ListBox
            className="overflow-auto max-h-[inherit] p-0 rounded-none border-none"
            items={items}>
            {children}
          </ListBox>
        </>
      </Popover>
    </PrimitiveMultiSelect>
  );
}
